import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const MAX_RETRY_ATTEMPTS = 5

const initialState = () => ({
  eventSource: null,
  retryAttempts: 0,
  toyUpdates: {},
  connectedStoreIds: [],
  isPageVisible: true,
  connectionLostWhileHidden: false,
  initialized: false
})

const state = initialState()

const mutations = {
  FORCE_CLOSE_EVENT_SOURCE(state) {
    if (state.eventSource) {
      console.warn('Fechando SSE manualmente (force close).')
      state.eventSource.close()
      state.eventSource = null
    }
  },
  SET_INITIALIZED(state, value) {
    state.initialized = value
  },
  SET_EVENT_SOURCE(state, eventSource) {
    state.eventSource = eventSource
  },
  SET_CONNECTED_STORE_IDS(state, storeIds) {
    state.connectedStoreIds = storeIds
  },
  CLOSE_EVENT_SOURCE(state) {
    if (state.eventSource) {
      state.eventSource.close()
      state.eventSource = null
    }
  },
  SET_RETRY_ATTEMPTS(state, attempts) {
    state.retryAttempts = attempts
  },
  SET_PAGE_VISIBILITY(state, isVisible) {
    state.isPageVisible = isVisible
  },
  SET_CONNECTION_LOST_WHILE_HIDDEN(state, value) {
    state.connectionLostWhileHidden = value
  },
  SET_TOY_UPDATE(state, payload) {
    Object.keys(payload).forEach((shoppingId) => {
      Vue.set(state?.toyUpdates, shoppingId, payload[shoppingId])
    })
  },
  PRUNE_TOY_UPDATES(state, allowedStoreIds) {
    const allowedIds = new Set((allowedStoreIds || []).map(String))

    Object.keys(state.toyUpdates).forEach((storeId) => {
      if (!allowedIds.has(String(storeId))) {
        Vue.delete(state.toyUpdates, storeId)
      }
    })
  },
  RESET_STATE(state) {
    Object.assign(state, initialState())
  }
}

const actions = {
  initVisibilityListeners({ commit, dispatch }) {
    document.addEventListener('visibilitychange', () => {
      const isVisible = document.visibilityState === 'visible'
      commit('SET_PAGE_VISIBILITY', isVisible)
      if (isVisible) {
        dispatch('checkAndReconnectIfNeeded')
      }
    })

    window.addEventListener('focus', () => {
      commit('SET_PAGE_VISIBILITY', true)
      dispatch('checkAndReconnectIfNeeded')
    })

    window.addEventListener('blur', () => {
      commit('SET_PAGE_VISIBILITY', false)
    })
  },
  checkAndReconnectIfNeeded({ commit, dispatch, state }) {
    console.log('checando e reconectando se necessario')
    if (state.connectionLostWhileHidden) {
      console.log('Reconectando apos pagina ficar visivel novamente...')
      commit('SET_CONNECTION_LOST_WHILE_HIDDEN', false)
      commit('SET_RETRY_ATTEMPTS', 0)
      dispatch('startSSE')
    } else if (
      !state.eventSource ||
      (state.eventSource && state.eventSource.readyState !== EventSource.OPEN)
    ) {
      console.log('Conexao SSE nao esta ativa, reconectando...')
      commit('SET_RETRY_ATTEMPTS', 0)
      dispatch('startSSE')
    }
  },
  syncWithCurrentUser({ dispatch, rootGetters, state, commit }) {
    const currentUserId = rootGetters['user/currentUserId']
    const userStores = rootGetters['user/currentUserStores'] || []
    const nextStoreIds = userStores.map((store) => store.id)
    const currentStoreIds = state.connectedStoreIds || []
    const normalize = (ids) => [...ids].map(String).sort().join(',')

    if (!currentUserId || nextStoreIds.length === 0) {
      commit('CLOSE_EVENT_SOURCE')
      commit('SET_CONNECTED_STORE_IDS', [])
      commit('PRUNE_TOY_UPDATES', [])
      return
    }

    commit('PRUNE_TOY_UPDATES', nextStoreIds)

    const storesChanged = normalize(nextStoreIds) !== normalize(currentStoreIds)
    const connectionIsOpen =
      state.eventSource && state.eventSource.readyState === EventSource.OPEN

    if (storesChanged || !connectionIsOpen) {
      dispatch('startSSE')
    }
  },
  startSSE({ commit, dispatch, rootGetters, state }) {
    const currentUserId = rootGetters['user/currentUserId']
    const userStores = rootGetters['user/currentUserStores']

    if (!currentUserId || !userStores || userStores.length === 0) {
      console.error('ID do usuario ou lojas nao definidas.')
      commit('CLOSE_EVENT_SOURCE')
      commit('SET_CONNECTED_STORE_IDS', [])
      commit('PRUNE_TOY_UPDATES', [])
      return
    }

    const storeIds = userStores.map((store) => store.id)
    console.log(
      'Conectando SSE para usuario:',
      currentUserId,
      'com as lojas:',
      storeIds
    )

    commit('CLOSE_EVENT_SOURCE')
    commit('SET_CONNECTED_STORE_IDS', storeIds)
    commit('PRUNE_TOY_UPDATES', storeIds)

    const apiUrl = process.env.VUE_APP_API_URL
    const url = `${apiUrl}/stores/stream/sse?userId=${encodeURIComponent(
      currentUserId
    )}&stores=${encodeURIComponent(JSON.stringify(storeIds))}`

    const eventSource = new EventSource(url)

    eventSource.onmessage = (event) => {
      try {
        const eventData = JSON.parse(event.data)
        console.log('Evento SSE recebido:', eventData)
        commit('SET_TOY_UPDATE', eventData)
        commit('SET_RETRY_ATTEMPTS', 0)
      } catch (err) {
        console.error('Erro ao parsear dados SSE:', err)
      }
    }

    eventSource.onopen = () => {
      if (state.retryAttempts > 0) {
        console.log('Reconectado ao SSE apos falha anterior!')

        window.dispatchEvent(
          new CustomEvent('sse:reconnected', {
            detail: {
              at: new Date(),
              attempts: state.retryAttempts
            }
          })
        )

        commit('SET_RETRY_ATTEMPTS', 0)
      }
    }

    eventSource.onerror = (error) => {
      console.error('Erro na conexao SSE:', error)
      commit('CLOSE_EVENT_SOURCE')

      const currentAttempts = state.retryAttempts + 1
      commit('SET_RETRY_ATTEMPTS', currentAttempts)

      if (currentAttempts <= MAX_RETRY_ATTEMPTS) {
        console.warn(
          `Tentando reconectar automaticamente... (Tentativa ${currentAttempts} de ${MAX_RETRY_ATTEMPTS})`
        )
        setTimeout(() => {
          dispatch('startSSE')
        }, 2000)
      } else {
        console.error(
          `Falha na reconexao apos ${MAX_RETRY_ATTEMPTS} tentativas. Recarregando a pagina forcadamente.`
        )
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    }
    commit('SET_EVENT_SOURCE', eventSource)
  },

  stopSSE({ commit }) {
    commit('CLOSE_EVENT_SOURCE')
    commit('SET_CONNECTED_STORE_IDS', [])
    commit('PRUNE_TOY_UPDATES', [])
    commit('SET_RETRY_ATTEMPTS', 0)
    commit('SET_CONNECTION_LOST_WHILE_HIDDEN', false)
  },
  init({ dispatch, commit, state }) {
    if (state.initialized) return
    commit('SET_INITIALIZED', true)
    dispatch('initVisibilityListeners')
    dispatch('syncWithCurrentUser')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
